import { useCallback, useEffect, useRef, useState } from 'react'
import { hasAcceptedTerms } from '../auth/PrimateAuthContext'
import { acceptTerms as acceptTermsApi, getUserProfile } from '../../core/lib/api/misc'

const TERMS_KEY = 'primate-terms-accepted'

/**
 * Window event dispatched (by useStreamSession) when the server rejects an
 * action with `terms_not_accepted`. Server truth beats any local mirror: the
 * gate must reopen so the user can actually (re-)consent — otherwise a stale
 * localStorage flag (accepted before the server-side consent column existed,
 * or a silently-failed PATCH) dead-ends every stream in a black RE-RUN card
 * with no way out (Matt + 2 live testers, prod, 2026-07-01).
 */
export const TERMS_REJECTED_EVENT = 'primate:terms-rejected'

/**
 * useTermsConsent — first-run Terms & Conditions gate state.
 *
 * Source of truth for "has this user accepted the terms?" combining:
 *   1. localStorage ('primate-terms-accepted') — fast, device-local.
 *   2. Server (user.terms_accepted_at via GET /v1/user/profile) — cross-device
 *      truth so a user who accepted on another device is never re-prompted.
 *
 * Returned `accepted`:
 *   - true   → consent recorded (local OR server). Render the app.
 *   - false  → definitively NOT accepted. Render the full-screen gate.
 *   - null   → still resolving (signed-in user, server fetch in flight). Caller
 *              should treat this as "don't block yet" to avoid a flash of the
 *              gate before the server confirms a cross-device acceptance.
 *
 * The gate only applies to signed-in, non-admin users. Anonymous/demo users and
 * admins are never blocked (admins are pre-trusted; anon users have no account
 * to persist consent against and are handled by the per-action gate + server
 * enforcement on upgrade).
 *
 * `markAccepted()` writes localStorage immediately, flips local state to
 * accepted synchronously (so the UI unblocks without a network wait), and
 * returns a promise that resolves once the server-side PATCH /v1/user/profile
 * has committed. Callers that immediately re-run a server-enforced action
 * (stream WS / upload) MUST await this promise, otherwise the action races the
 * consent write and the server rejects it as not-yet-accepted (read-your-own-
 * write race — PRI consent-firstprompt bug, 2026-06-26). The PATCH is
 * idempotent; on PATCH failure the promise still resolves (local + enforcement
 * remain the backstop) so Accept never hangs the UI.
 */
export function useTermsConsent(opts: {
  isSignedIn: boolean
  isAdmin: boolean
  authLoaded: boolean
  /** When true (dev/test bypass), skip the server fetch and use localStorage only. */
  devBypass?: boolean
  /**
   * Test-only escape hatch: when true, the first-run mount gate is evaluated even
   * under devBypass (sourced from localStorage only, no server probe). Lets the
   * dedicated first-run-gate e2e spec exercise the gate while the legacy
   * per-action TermsModal specs (which rely on devBypass skipping the gate) stay
   * green. Set via localStorage 'primate-dev-firstrun-gate' = '1'.
   */
  forceGateUnderBypass?: boolean
  /**
   * When true, an anonymous (signed-out) user is ALSO gated until they accept
   * the T&Cs. This covers invited-anon users who reach the app Home via the
   * /invite flow without creating an account — they must acknowledge the terms
   * before they can use the camera / prompt. Sourced from localStorage only
   * (no server probe; an anon user has no account to fetch). Admins and the
   * dev/test signed-in bypass are still never gated.
   */
  gateAnonInvited?: boolean
}): {
  /** true = accepted, false = not accepted, null = resolving */
  accepted: boolean | null
  /** Whether the full-screen first-run gate should block the app. */
  gateActive: boolean
  /**
   * Records consent. Returns a promise that resolves AFTER the server PATCH
   * settles — await it before re-running any server-enforced action (stream /
   * upload) to avoid the read-your-own-write race.
   */
  markAccepted: () => Promise<void>
} {
  const { isSignedIn, isAdmin, authLoaded, devBypass, forceGateUnderBypass, gateAnonInvited } = opts
  // Under the test opt-in, treat the session as NOT dev-bypassed for gating
  // purposes so the mount gate engages (still localStorage-sourced; no server call).
  const effectiveBypass = devBypass && !forceGateUnderBypass

  // Seed from localStorage synchronously so a returning user never flashes the gate.
  const [localAccepted, setLocalAccepted] = useState<boolean>(() => hasAcceptedTerms())
  // Server value: undefined = not yet fetched, true/false = known, null treated as false.
  const [serverAccepted, setServerAccepted] = useState<boolean | undefined>(undefined)
  // Set when markAccepted() runs, so a slow in-flight profile probe (read
  // before the PATCH committed) can't clobber a just-given consent.
  const justAcceptedRef = useRef(false)

  // Fetch server truth once for a real signed-in session (skip under dev bypass,
  // where getToken is a no-op and no server profile exists).
  //
  // Runs even when localAccepted is true (2026-07-01 fix): a stale local flag
  // used to short-circuit this probe, so a user whose server row had
  // terms_accepted_at = null (consented before the server-side column shipped,
  // or the PATCH failed silently) was never re-gated — and every stream was
  // rejected server-side with terms_not_accepted and no recovery path. Local
  // stays authoritative-optimistic while the probe is in flight (no gate
  // flash, pre-connect unblocked); the server result then reconciles.
  useEffect(() => {
    // Skip the server probe under any bypass (including the forced-gate test
    // opt-in, which is localStorage-sourced only).
    if (!authLoaded || !isSignedIn || isAdmin || devBypass) return
    let cancelled = false
    getUserProfile()
      .then(profile => {
        if (cancelled || justAcceptedRef.current) return
        const accepted = profile?.terms_accepted_at != null
        setServerAccepted(accepted)
        if (accepted) {
          // Mirror server acceptance into localStorage so future loads are instant.
          try { localStorage.setItem(TERMS_KEY, profile.terms_accepted_at as string) } catch { /* ignore */ }
          setLocalAccepted(true)
        } else {
          // Server truth wins for signed-in users: clear the stale local
          // mirror so the first-run gate re-engages and consent can actually
          // be recorded server-side.
          try { localStorage.removeItem(TERMS_KEY) } catch { /* ignore */ }
          setLocalAccepted(false)
        }
      })
      .catch(() => {
        // Probe error: fail closed ONLY when local doesn't claim acceptance
        // (a network blip must not lock out an already-consented user — the
        // resolution below lets localAccepted win; server enforcement remains
        // the backstop).
        if (!cancelled) setServerAccepted(false)
      })
    return () => { cancelled = true }
  }, [authLoaded, isSignedIn, isAdmin, devBypass])

  // Server-enforcement feedback loop: a live `terms_not_accepted` rejection
  // (stream WS auth gate / upload 403) proves the server has no consent on
  // record regardless of what any local mirror claims. Re-gate immediately.
  useEffect(() => {
    const onRejected = () => {
      justAcceptedRef.current = false
      try { localStorage.removeItem(TERMS_KEY) } catch { /* ignore */ }
      setLocalAccepted(false)
      setServerAccepted(false)
    }
    window.addEventListener(TERMS_REJECTED_EVENT, onRejected)
    return () => window.removeEventListener(TERMS_REJECTED_EVENT, onRejected)
  }, [])

  // Under the forced-gate test opt-in there is no server probe, so resolve the
  // server value deterministically from localStorage (absent => not accepted).
  useEffect(() => {
    if (forceGateUnderBypass && devBypass) {
      setServerAccepted(localAccepted)
    }
  }, [forceGateUnderBypass, devBypass, localAccepted])

  const markAccepted = useCallback(async () => {
    justAcceptedRef.current = true
    try { localStorage.setItem(TERMS_KEY, new Date().toISOString()) } catch { /* ignore */ }
    setLocalAccepted(true)
    setServerAccepted(true)
    // Persist server-side (idempotent) and AWAIT it. A caller that re-runs a
    // server-enforced action (stream WS auth / upload) right after Accept must
    // not start before terms_accepted_at is committed in the DB — otherwise the
    // server reads null and silently rejects the very action just unblocked.
    // We swallow PATCH errors (resolve, not reject) so Accept never hangs: the
    // local flag + server enforcement on the next attempt remain the backstop.
    await acceptTermsApi().catch(() => { /* idempotent; enforcement is the backstop */ })
  }, [])

  // An invited anon user (signed-out, not admin, not dev-bypassed) is gated
  // purely on localStorage — they have no account to probe server-side.
  const anonInvitedGated = !!gateAnonInvited && !isSignedIn && !isAdmin && !effectiveBypass

  // Resolve combined accepted state.
  let accepted: boolean | null
  if (localAccepted) {
    accepted = true
  } else if (anonInvitedGated) {
    // Invited anon, terms not accepted locally → definitively not accepted.
    accepted = false
  } else if (!isSignedIn || isAdmin || effectiveBypass) {
    // Not gated for plain anon/admin/dev — treat as accepted (don't block).
    accepted = true
  } else if (serverAccepted === undefined) {
    accepted = null // resolving
  } else {
    accepted = serverAccepted
  }

  // The gate blocks when we have a definitive "not accepted" for either a real
  // signed-in non-admin user OR an invited anon user. While resolving (null) we
  // do NOT block.
  const gateActive = authLoaded
    && !isAdmin
    && !effectiveBypass
    && (isSignedIn || anonInvitedGated)
    && accepted === false

  return { accepted, gateActive, markAccepted }
}
